/**
 * Client-side Not Found surface.
 *
 * Stops unknown SPA paths from soft-404ing to the landing page (SEO
 * framework §15.2 / ADR 0001). Note: nginx SPA fallback still returns HTTP
 * 200 for the shell; true edge 404s require SSR or edge rules later.
 */
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { usePageHeader } from "../layouts/PageHeaderContext";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { BRAND_ORG, BRAND_WORDMARK } from "../theme/brand";

export function NotFoundPage() {
	usePageHeader({
		title: "Page not found",
		subtitle: "This address is not a published FlahaSOIL page",
	});

	return (
		<Box
			component="main"
			id="main-content"
			data-testid="not-found-surface"
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				minHeight: "60vh",
				py: { xs: 4, md: 8 },
			}}
		>
			<GlassSurface
				intensity="strong"
				depth="floating"
				sx={{ p: { xs: 3, sm: 4 }, maxWidth: 520, width: "100%" }}
			>
				<Stack spacing={2.5}>
					<Typography
						variant="overline"
						sx={{ color: "workspace.green", letterSpacing: "0.12em" }}
					>
						{BRAND_WORDMARK} · 404
					</Typography>
					<Typography component="h1" variant="h3">
						Page not found
					</Typography>
					<Typography sx={{ color: "workspace.textSecondary" }}>
						This URL is not a published product page. Private workspace
						routes require sign-in, and removed or mistyped links do not
						redirect to the homepage (to avoid soft-404s in search).
					</Typography>
					<Typography variant="body2" sx={{ color: "workspace.textMuted" }}>
						A product of {BRAND_ORG}.
					</Typography>
					<Stack direction="row" flexWrap="wrap" gap={1.5}>
						<Button component={RouterLink} to="/" variant="contained">
							FlahaSOIL home
						</Button>
						<Button
							component={RouterLink}
							to="/standards"
							variant="outlined"
							sx={{
								color: "workspace.textPrimary",
								borderColor: "workspace.borderStrong",
							}}
						>
							Scientific standards
						</Button>
						<Button
							component={RouterLink}
							to="/login"
							variant="text"
							sx={{ color: "workspace.textSecondary" }}
						>
							Sign in
						</Button>
					</Stack>
				</Stack>
			</GlassSurface>
		</Box>
	);
}
